import { Button, message, Modal, Input, Spin } from "antd";
import { AuthContext } from "@modules/auth/contexts/auth-plus.context";
import { useContext, useState } from "react";
import { useConfirmModal } from "@modules/shared/hooks/use-confirm-modal.hook";
import { VerifyPackageConfirmModal } from "../VerifyPackageConfirmModal/VerifyPackageConfirmModal";
import { PackageService } from "@modules/verification/trainer-package/services/TrainerPackageVerificationService";

export function PackageDetailModal({
  pkg,
  onClose,
  onSendMessage,
  refreshPackages,
}: {
  pkg: any;
  onClose: () => void;
  onSendMessage?: () => void;
  refreshPackages?: () => void;
}) {
  const { auth, setAuth, logout } = useContext(AuthContext);
  const [loadingVerify, setLoadingVerify] = useState(false);
  const [loadingReject, setLoadingReject] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectInput, setShowRejectInput] = useState(false);
  const { showConfirm } = useConfirmModal();

  const handleVerify = async () => {
    if (!auth || !pkg?.id) return;

    showConfirm(
      <VerifyPackageConfirmModal />,
      "Verify",
      "primary",
      async () => {
        try {
          setLoadingVerify(true);
          await PackageService.verifyPackage(auth, setAuth, logout, pkg.id);
          message.success("Package verified successfully!");
          refreshPackages?.();
          onClose();
        } catch (err: any) {
          message.error(err.message || "Failed to verify package");
        } finally {
          setLoadingVerify(false);
        }
      },
    );
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      message.warning("Please enter a reason for rejection.");
      return;
    }
    if (!auth || !pkg?.id) return;

    setLoadingReject(true);
    try {
      await PackageService.rejectPackage(
        auth,
        setAuth,
        logout,
        pkg.id,
        rejectReason.trim(),
      );
      message.success("Package rejected");
      refreshPackages?.();
      onClose();
    } catch (err: any) {
      message.error(err.message || "Failed to reject package");
    } finally {
      setLoadingReject(false);
      setShowRejectInput(false);
      setRejectReason("");
    }
  };

  return (
    <Modal
      open={!!pkg}
      onCancel={onClose}
      title={pkg?.title}
      footer={[
        <Button
          key="verify"
          type="primary"
          onClick={handleVerify}
          loading={loadingVerify}
          disabled={pkg?.verified_package}
        >
          Approve
        </Button>,
        !showRejectInput ? (
          <Button key="reject" danger onClick={() => setShowRejectInput(true)}>
            Reject
          </Button>
        ) : (
          <Button key="reject-cancel" onClick={() => setShowRejectInput(false)}>
            Cancel Reject
          </Button>
        ),
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
      width={800}
    >
      {pkg && (
        <>
          <img
            alt=""
            src={pkg.image ?? "/no-image.jpg"}
            className="rounded mb-3"
            style={{
              width: "50%",
              maxHeight: "400px",
              maxWidth: "300px",
              height: "auto",
              objectFit: "cover",
              display: "block",
              borderRadius: "8px",
            }}
          />

          <p>
            <strong>Description:</strong>
          </p>
          <p>{pkg.description || "No description available."}</p>

          <hr />

          <p>
            <strong>Category:</strong> {pkg.categories}
          </p>
          <p>
            <strong>Price:</strong> {pkg.price} {pkg.currency.toUpperCase()}
          </p>
          <p>
            <strong>Sessions:</strong> {pkg.session_count} ×{" "}
            {pkg.session_duration} mins
          </p>
          <p>
            <strong>Validity:</strong> {pkg.validity_period} days
          </p>
          <p>
            <strong>Created At:</strong>{" "}
            {new Date(pkg.created_at).toLocaleString()}
          </p>

          {showRejectInput && (
            <div
              className="mt-3 p-3 border rounded"
              style={{ backgroundColor: "#fff5f5" }}
            >
              <label className="form-label fw-semibold">
                Reason for Rejection
              </label>
              <Input.TextArea
                rows={3}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason here..."
              />
              <div className="mt-2 d-flex justify-content-end gap-2">
                <Button
                  type="primary"
                  danger
                  onClick={handleReject}
                  loading={loadingReject}
                >
                  Confirm Reject
                </Button>
                <Button
                  onClick={() => {
                    setShowRejectInput(false);
                    setRejectReason("");
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
